import BigNumber from 'bignumber.js';

import SETTINGS from '#/config/global-settings';

import { Market } from '#/api/markets';
import { MarketSummary } from '#/api/markets-summary';
import { OrderInstruction, OrderType } from '#/api/orders';

import * as MarketFn from '#/features/market/functions';

import { formatUsd } from '#/utils/fiat';

import { OrderFormState, OrderTimeInForce } from './form-state';

type FeeType = 'TAKER' | 'MAKER';

/** Maximum fee user is willing to pay */
function getOrderFeePercent(type: FeeType): BigNumber {
  return {
    MAKER: BigNumber(MAKER_FEE),
    TAKER: BigNumber(TAKER_FEE),
  }[type];
}

const ORDER_FEE_PRECISION = 4;

interface TimeInForceOption {
  readonly value: OrderTimeInForce;
  readonly selected: boolean;
}

export interface OrderFormView {
  readonly asset: string | null;
  readonly amount: FieldView;
  readonly limitPrice: FieldView;
  readonly type: OrderType;

  // Derived:
  readonly isValid: boolean;
  readonly amountBaseAsset: FormattedView;
  readonly amountDecimalPlaces: number;
  readonly limitPriceDecimalPlaces: number;
  readonly notionalQuoteAsset: FormattedView;
  readonly limitFee: FormattedView;
  readonly feeRange: string;
  readonly isPostOnly: boolean;
  readonly timeInForce: {
    readonly value: OrderTimeInForce;
    readonly isDisabled: boolean;
    readonly options: readonly TimeInForceOption[];
  };
  readonly instruction: OrderInstruction;
  readonly errors: readonly string[];
}

interface FormattedView {
  /** raw value
   * @example '30000.5'
   * @example '0.34'
   */
  readonly value: string;
  /**
   * @example '$30,000.50'
   * @example '0.3400 BTC'
   */
  readonly formattedValue: string;
}

interface FieldView extends FormattedView {
  readonly isValid: boolean;
  readonly error: string;
}

// WIP: needs to be covered with unit tests
export function createView(
  state: OrderFormState,
  activeMarket: Market | null,
  activeMarketSummary: MarketSummary | undefined,
  isSignedIn: boolean,
  isBeingLiquidated: boolean,
): OrderFormView {
  const price = priceView(state, activeMarketSummary);
  const amount = amountView(state, activeMarket);
  const amountBaseAsset = amountBaseAssetView(state, activeMarket, price);
  const amountDecimalPlaces = amountDecimalPlacesView(state, activeMarket);
  const limitPriceDecimalPlaces = limitPriceDecimalPlacesView(activeMarket);
  const limitPrice = limitPriceView(state);
  const limitFee = limitFeeView(state, activeMarket, price);
  const feeRange = feeRangeView(state, activeMarket, price);
  const notionalQuoteAsset = notionalQuoteAssetView(state, activeMarket, price);
  const isPostOnly = isPostOnlyView(state);
  const timeInForce = timeInForceView(state);
  const instruction = instructionView(state);
  const errors = errorsView();
  const isValid = isValidView();

  return {
    asset: state.asset,
    type: state.type,
    isPostOnly,
    timeInForce,
    instruction,
    amount,
    amountBaseAsset,
    amountDecimalPlaces,
    limitPriceDecimalPlaces,
    notionalQuoteAsset,
    limitPrice,
    limitFee,
    feeRange,
    isValid,
    errors,
  };

  function errorsView() {
    return [
      limitPrice.error,
      amount.error,
      !isSignedIn ? 'Please connect your wallet to place an order.' : '',
      isBeingLiquidated
        ? 'Cannot place an order while account is in liquidation.'
        : '',
    ].filter((error) => error.length > 0);
  }

  function isValidView() {
    return (
      [limitPrice, amount].every((field) => field.isValid) &&
      errors.length === 0
    );
  }
}

function priceView(
  state: OrderFormState,
  activeMarketSummary: MarketSummary | undefined,
) {
  return state.type === 'MARKET'
    ? activeMarketSummary?.oracle_price ?? null
    : state.limitPrice ?? null;
}

function isPostOnlyView(state: OrderFormState): OrderFormView['isPostOnly'] {
  return state.isPostOnly;
}

function instructionView(state: OrderFormState): OrderFormView['instruction'] {
  switch (state.type) {
    case 'MARKET': {
      return 'GTC';
    }
    case 'LIMIT': {
      if (state.isPostOnly) return 'POST_ONLY';
      return state.timeInForce;
    }
    // no default
  }
}

function timeInForceView(state: OrderFormState): OrderFormView['timeInForce'] {
  return {
    value: state.timeInForce,
    isDisabled: state.isPostOnly,
    options: timeInForceOptionsView(state),
  };
}

function timeInForceOptionsView(
  state: OrderFormState,
): OrderFormView['timeInForce']['options'] {
  return [
    {
      value: 'GTC',
      selected: state.timeInForce === 'GTC',
    },
    {
      value: 'IOC',
      selected: state.timeInForce === 'IOC',
    },
  ];
}

function limitPriceView({
  limitPrice,
  type,
}: OrderFormState): OrderFormView['limitPrice'] {
  if (type === 'MARKET') {
    return {
      value: '',
      formattedValue: '-',
      isValid: true,
      error: '',
    };
  }

  if (limitPrice == null) {
    return {
      value: '',
      formattedValue: '-',
      isValid: false,
      error: 'Please enter your order limit price.',
    };
  }

  return {
    value: limitPrice.toString(),
    formattedValue: `$${limitPrice.toFormat()}`,
    isValid: limitPrice.isGreaterThan(0),
    error: limitPrice.isGreaterThan(0)
      ? ''
      : 'Order limit price must be greater than 0.',
  };
}

function amountView(
  { amount, asset }: OrderFormState,
  activeMarket: Market | null,
): OrderFormView['amount'] {
  if (activeMarket == null) {
    return {
      value: '',
      formattedValue: '-',
      isValid: false,
      error: 'Order form is not ready yet. Please try again.',
    };
  }

  if (amount == null) {
    return {
      value: '',
      formattedValue: '-',
      isValid: false,
      error: 'Please enter your order amount.',
    };
  }

  return {
    value: amount.toString(),
    formattedValue:
      asset === activeMarket.quote_currency
        ? `$${amount.toFormat()}`
        : `${amount.toFormat()} ${asset}`,
    isValid: amount.isGreaterThan(0),
    error: amount.isGreaterThan(0)
      ? ''
      : 'Order amount must be greater than 0.',
  };
}

function amountBaseAssetView(
  { amount, asset }: OrderFormState,
  activeMarket: Market | null,
  price: BigNumber | null,
): OrderFormView['amountBaseAsset'] {
  if (activeMarket == null || amount == null || price == null) {
    return {
      value: '',
      formattedValue: '-',
    };
  }

  const amountBaseAsset = convertToBaseAsset(
    asset,
    amount,
    price,
    activeMarket,
  );

  if (amountBaseAsset == null) {
    return {
      value: '',
      formattedValue: '-',
    };
  }

  return {
    value: amountBaseAsset.toString(),
    formattedValue: `${amountBaseAsset.toFormat(
      MarketFn.baseCurrencyDecimalPlaces(activeMarket),
    )} ${activeMarket.base_currency}`,
  };
}

function amountDecimalPlacesView(
  { amount, asset }: OrderFormState,
  activeMarket: Market | null,
): OrderFormView['amountDecimalPlaces'] {
  if (activeMarket == null || amount == null) {
    return 0;
  }

  if (asset === activeMarket.base_currency) {
    return MarketFn.baseCurrencyDecimalPlaces(activeMarket);
  }

  if (asset === activeMarket.quote_currency) {
    return MarketFn.quoteCurrencyDecimalPlaces(activeMarket);
  }

  return 0;
}

function limitPriceDecimalPlacesView(
  activeMarket: Market | null,
): OrderFormView['amountDecimalPlaces'] {
  if (activeMarket == null) {
    return 0;
  }

  return MarketFn.quoteCurrencyDecimalPlaces(activeMarket);
}

function notionalQuoteAssetView(
  { amount, asset }: OrderFormState,
  activeMarket: Market | null,
  price: BigNumber | null,
): OrderFormView['notionalQuoteAsset'] {
  if (activeMarket == null || amount == null || price == null) {
    return {
      value: '',
      formattedValue: '-',
    };
  }

  const notionalQuoteAsset = convertToQuoteAsset(
    asset,
    amount,
    price,
    activeMarket,
  );
  return {
    value: notionalQuoteAsset.toString(),
    formattedValue: `$${notionalQuoteAsset.toFormat(SETTINGS.fiat.USD.scale)}`,
  };
}

function limitFeeView(
  { amount, asset }: OrderFormState,
  activeMarket: Market | null,
  price: BigNumber | null,
): OrderFormView['limitFee'] {
  if (activeMarket == null || amount == null || price == null) {
    return {
      value: '',
      formattedValue: '-',
    };
  }

  const amountQuoteAsset = convertToQuoteAsset(
    asset,
    amount,
    price,
    activeMarket,
  );
  const limitFee = calcLimitFee(amountQuoteAsset, 'TAKER');
  return {
    value: limitFee.toString(),
    formattedValue: `$${limitFee.toFormat(ORDER_FEE_PRECISION)}`,
  };
}

function feeRangeView(
  { amount, asset }: OrderFormState,
  activeMarket: Market | null,
  price: BigNumber | null,
): OrderFormView['feeRange'] {
  if (activeMarket == null || amount == null || price == null) {
    return '-';
  }

  const amountQuoteAsset = convertToQuoteAsset(
    asset,
    amount,
    price,
    activeMarket,
  );
  const makerFee = calcLimitFee(amountQuoteAsset, 'MAKER');
  const takerFee = calcLimitFee(amountQuoteAsset, 'TAKER');

  const makerFeeFormatted = formatUsd(makerFee, FEE_FORMATTING);
  const takerFeeFormatted = formatUsd(takerFee, FEE_FORMATTING);

  return `${makerFeeFormatted} / ${takerFeeFormatted}`;
}

const FEE_FORMATTING = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 4,
};

function convertToQuoteAsset(
  asset: string | null,
  amount: BigNumber,
  price: BigNumber,
  activeMarket: Market,
): BigNumber {
  if (asset == null) {
    return amount;
  }

  if (asset === activeMarket.base_currency) {
    return amount.multipliedBy(price);
  }

  if (asset === activeMarket.quote_currency) {
    return amount;
  }

  throw new Error(
    `${asset} does not match market ${activeMarket.base_currency}/${activeMarket.quote_currency} asset`,
  );
}

function convertToBaseAsset(
  asset: string | null,
  amount: BigNumber,
  price: BigNumber,
  activeMarket: Market,
): BigNumber | null {
  if (asset == null) {
    return amount;
  }

  if (asset === activeMarket.base_currency) {
    return amount;
  }

  if (asset === activeMarket.quote_currency) {
    const amountBaseAsset = amount.dividedBy(price);
    const value = amountBaseAsset.isFinite()
      ? amountBaseAsset.decimalPlaces(
          MarketFn.baseCurrencyDecimalPlaces(activeMarket),
        )
      : null;

    return value;
  }

  throw new Error(
    `${asset} does not match market ${activeMarket.base_currency}/${activeMarket.quote_currency} asset`,
  );
}

function calcLimitFee(amountQuoteAsset: BigNumber, type: FeeType) {
  return amountQuoteAsset
    .multipliedBy(getOrderFeePercent(type))
    .decimalPlaces(ORDER_FEE_PRECISION, BigNumber.ROUND_UP);
}
